import React, { ChangeEvent, useRef, useState } from 'react';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import JabraInput from 'components/Shared/JabraInput';

import { useFormTheme } from '../../contexts/FormTheme/FormThemeContext';
import { usePrefillableValue } from '../../hooks/usePrefillableValue';
import { EmailWithConfirmationViewModel } from '../../types/emailWithConfirmation';

const EmailWithConfirmation: React.FunctionComponent<
  ValueFieldProps<EmailWithConfirmationViewModel>
> = (props) => {
  const { errors, field, ...restProps } = props;

  const [emailValue, setEmailValue] = usePrefillableValue(
    field.model.name,
    props.value,
    props.onChange,
    field.valueField.name
  );
  const [confirmEmailValue, setConfirmEmailValue] = useState('');
  const [confirmEmailErrors, setConfirmEmailErrors] = useState<string[]>([]);

  const emailValueRef = useRef(emailValue);
  emailValueRef.current = emailValue;

  const FieldValidationErrors = props.fieldValidationErrorsComponent;
  const Label = props.labelComponent;
  const { primaryTextColor } = useFormTheme();

  const validateEmails = (email: string, confirmEmail: string): string[] => {
    if (!confirmEmail) return [];
    if (email !== confirmEmail) {
      return [field.model.validationErrorMessage || 'Email addresses do not match'];
    }
    return [];
  };

  const onEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event?.target?.value;
    setEmailValue(newValue);
    emailValueRef.current = newValue;

    const validationErrors = validateEmails(newValue, confirmEmailValue);
    setConfirmEmailErrors(validationErrors);

    const isValid = validationErrors.length === 0;
    props.onChange(field.valueField.name, newValue, isValid, []);
  };

  const onConfirmEmailChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newValue = event?.target?.value;
    setConfirmEmailValue(newValue);

    const validationErrors = validateEmails(emailValueRef.current, newValue);
    setConfirmEmailErrors(validationErrors);

    const isValid = validationErrors.length === 0;

    const confirmEmailFieldName = field.valueField.name.replace('.Value', '.ConfirmEmail');
    props.onChange(confirmEmailFieldName, newValue, isValid, []);
    props.onChange(field.valueField.name, emailValueRef.current, isValid, validationErrors);
  };

  const confirmFieldId = `${field.valueField.id}_confirm`;
  const confirmFieldName = field.valueField.name.replace('.Value', '.ConfirmEmail');
  const confirmLabel = field.model.confirmEmailLabel || 'Confirm Email';
  const confirmPlaceholder = field.model.confirmEmailPlaceholder || field.model.placeholderText;

  return (
    <div className="flex flex-col gap-4">
      {/* Email Field */}
      <div>
        {!!Label && <Label {...props} />}
        <JabraInput
          type="email"
          id={field.valueField.id}
          name={field.valueField.name}
          value={emailValue}
          jssMetadata={props}
          placeholder={field.model.placeholderText}
          required={field.model.required}
          onChange={onEmailChange}
        />
        {!!FieldValidationErrors && errors.length > 0 && (
          <FieldValidationErrors
            field={field}
            errors={errors.map((error) =>
              !!field.model.validationErrorMessage ? field.model.validationErrorMessage : error
            )}
            {...restProps}
          />
        )}
      </div>

      {/* Confirm Email Field */}
      <div>
        <div className={classNames('mb-2 text-sm font-medium', primaryTextColor)}>
          <label htmlFor={confirmFieldId}>{confirmLabel}</label>
          {field.model.required && <span className="pl-0.5 font-medium text-error">*</span>}
        </div>
        <JabraInput
          type="email"
          id={confirmFieldId}
          name={confirmFieldName}
          value={confirmEmailValue}
          jssMetadata={{ ...props, errors: confirmEmailErrors }}
          placeholder={confirmPlaceholder}
          required={field.model.required}
          onChange={onConfirmEmailChange}
        />
        {!!FieldValidationErrors && confirmEmailErrors.length > 0 && (
          <FieldValidationErrors field={field} errors={confirmEmailErrors} {...restProps} />
        )}
      </div>
    </div>
  );
};

export default EmailWithConfirmation;
